<template>
    <div id="ppanel-resources"
         class="ppanel-listing"
    >
        <div class="no-data"
             v-if="!data.resourceList.length"
        >
           {{ i18n.t("info_no_teaching_resources")}}
        </div>
        <div class="item"
             v-for="item in data.resourceList"
        >
            <a href="#"
               @click.prevent="launchResource(item.filename)"
            >
                <span class="resource-icon"
                      :class="classResourceIcon(item.ico)"
                >
                   {{ data.resourceIcons[item.ico] }}
                </span>
                <span class="label">
                        {{ item.title }}
                </span>
            </a>
        </div>

    </div>
</template>

<script>
    import {ref, reactive, onMounted, onUpdated, watchEffect, watch, computed, inject}  from "vue";
    import {useDataLoader} from "@/modules/reader/book/dataLoader";
    import {usePpanel}  from "@/modules/reader/ppanel/ppanel";
    import {appendAccessToken} from  "@/modules/utils/pathUtils";
    import {usePaging} from "@/modules/reader/book/paging";
    import {useBookInfo} from "@/modules/reader/book/bookinfo";
    import {uniqueArrayOfObject} from "@/modules/utils/arrayUtils";
    import {isTeacher, isStudent} from "@/modules/api/auth";
    import axios from 'axios'
    import qs from "qs";
    import cssvar from '@/scss/_variables.scss';


    export default {
        setup(props, context) {

            const i18n = inject('i18n');
            const { bookData} = useDataLoader();
            const {pPanelState} = usePpanel();
            const { pageState } = usePaging();
            const {bookInfoState} = useBookInfo();

            const data = reactive({
                heading: i18n.t("label_teaching_resources"),
                resourceIcons:{
                    avi: "MP4",
                    mov: "MOV",
                    mp3: "MP3",
                    ws: "DOC",
                    doc: "DOC",
                    docx: "DOC",
                    pdf: "PDF",
                    ppt: "PPT",
                    pptx: "PPT",
                    ppt: "PPT",
                    html: "HTML",
                    htm: "HTM",
                    web: "HTML",
                    xls: "XLS",
                    xlsx: "XLS",
                    png: "IMG",
                    gif: "IMG",
                    jpg: "IMG",
                    img: "IMG",
                    txt:"TXT",
                    zip:"ZIP"
                },
                resourceList:[],
            })

            const classResourceIcon = (ico)=> {
                return {
                    'icon-mp3': (ico === "mp3"),
                    'icon-mp4': (ico === "avi"),
                    'icon-mov': (ico === "mov"),
                    'icon-doc': (ico === "ws") || (ico === "doc") || (ico === "docx"),
                    'icon-pdf': (ico === "pdf"),
                    'icon-ppt': (ico === "ppt") || (ico === "pptx"),
                    'icon-png': (ico === "png"),
                    'icon-jpg': (ico === "jpg"),
                    'icon-gif': (ico === "gif"),
                    'icon-img': (ico === "img"),
                    'icon-html': (ico === "html") || (ico === "htm"),
                    'icon-txt': (ico === "txt"),
                    'icon-xls': (ico === "xls") || (ico === "xlsx"),
                    'icon-zip': (ico === "zip")
                }
            }

            const launchResource = (filename)=>{

                let expression = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/g
                let regex = new RegExp(expression);

                if (filename.match(regex)) {
                    window.open(filename, '_blank');
                } else {
                    window.open(appendAccessToken(`${bookInfoState.ebookDownloadPath}/${filename}`), '_blank');
                }
            }

            const setMenuIsEnabled = ()=>{

                if (!data.resourceList) {
                    pPanelState.isResourcesEnabled = false;
                    return;
                }
                pPanelState.isResourcesEnabled =  data.resourceList.length > 0;
            }

            watchEffect(()=>{

                let resKeyName = isTeacher() ? "offline_resource_t" : "offline_resource_s";

                if (!pageState.isRightPageAvailable) {

                    data.resourceList = [];
                    if (bookData.resources[bookData.pagenames[pageState.currentPage[0]]]) {
                        let item =  bookData.resources[bookData.pagenames[pageState.currentPage[0]]][resKeyName];
                        if (item) {
                            data.resourceList = item;
                        }
                    }
                    setMenuIsEnabled();
                } else {
                    let leftPageResources, rightPageResources;

                    data.resourceList = [];

                    if (bookData.resources[bookData.pagenames[pageState.currentPage[0]]]) {
                        leftPageResources = bookData.resources[bookData.pagenames[pageState.currentPage[0]]][resKeyName];
                    }

                    //Note that the right hand side page can be unavailable if total page number is odd and the last page has reached;
                    //so we check both conditions:
                    if (pageState.currentPage[1] && bookData.resources[bookData.pagenames[pageState.currentPage[1]]])
                    {
                        rightPageResources = bookData.resources[bookData.pagenames[pageState.currentPage[1]]][resKeyName];
                    }

                    if (leftPageResources && rightPageResources) {
                        data.resourceList = leftPageResources.concat(rightPageResources);
                        //output unique resource items since items can duplicate on both pages
                        data.resourceList = uniqueArrayOfObject(data.resourceList);
                    } else {
                        if (leftPageResources) {
                            data.resourceList = leftPageResources;
                        }
                        if (rightPageResources) {
                            data.resourceList = rightPageResources;
                        }
                    }
                    setMenuIsEnabled();
                }

            })

            return {
                i18n,
                data,
                bookData,
                classResourceIcon,
                launchResource
            }
        }

    }

</script>

<style scoped lang="scss">

    .resource-icon {
        margin: 0 10px 0 0;
        font-size: 13px;
        padding: 2px 4px;
        color: #fff;
        height: 20px;
        text-align: center;
        border-radius: 4px;
        flex: 0 0 40px;
    }

    .icon-mp4 {
        background:$color-res-mp4 !important;
    }

    .icon-mp3 {
        background:$color-res-mp3 !important;
    }

    .icon-mov {
        background:$color-res-mov !important;
    }

    .icon-pdf {
        background:$color-res-pdf !important;
    }

    .icon-doc {
        background:$color-res-doc !important;
    }

    .icon-ppt {
        background:$color-res-ppt !important;
    }

    .icon-jpg, .icon-gif, .icon-img,  .icon-png  {
        background:$color-res-img !important;
    }

    .icon-xls {
        background:$color-res-xls !important;
    }

    .icon-html {
        background:$color-res-html !important;
    }

    .icon-txt {
        background:$color-res-txt !important;
    }

    .icon-zip {
        background:$color-res-zip !important;
    }


</style>